/**
 * Display model for the Appraisal Delivery report.
 *
 * Classification is NOT decided here. It comes from the shared contract in
 * `supabase/functions/_shared/appraisal-delivery-contract.ts`, which the edge
 * dispatcher also consumes, so an operator reading this screen sees exactly the
 * state that decides whether they get emailed. This file previously carried its
 * own crosswalk that had already drifted from the server's.
 */

import {
  resolveAppraisalDeliveryState,
  APPRAISAL_PIPELINE_LABELS,
  APPRAISAL_STATE_LABELS,
  type AppraisalDeliveryState,
  type AppraisalLifecycleState,
  type AppraisalPipeline as SharedAppraisalPipeline,
  type AppraisalStage
} from '../../../supabase/functions/_shared/appraisal-delivery-contract';

export {
  APPRAISAL_PIPELINE_LABELS,
  APPRAISAL_STATE_LABELS
};
/** Audit data is served by a loan-authorized Edge snapshot, outside this legacy delivery report. */
export type AppraisalPipeline = Exclude<SharedAppraisalPipeline, 'appraisal_audit'>;
export type { AppraisalLifecycleState, AppraisalStage };
/** Long-form alias kept so report call sites read consistently. */
export type AppraisalDeliveryPipeline = AppraisalPipeline;

export interface AppraisalDeliveryQueueRecord {
  id: string;
  pipeline: AppraisalPipeline;
  encompassLoanId: string;
  attachmentId: string;
  status: string;
  skipReason: string | null;
  validationDetails: Record<string, unknown> | null;
  scheduledFor: string | null;
  createdAt: string;
  /** Null when the source table has no updated_at column. Never faked. */
  updatedAt: string | null;
  /** Terminal decision timestamp for queue tables that do not have updated_at. */
  decidedAt: string | null;
  emailSentAt: string | null;
  borrowerEmails: string[];
  ccEmails: string[];
  resendId: string | null;
  receiptRetryCount: number;
  receiptNextRetryAt: string | null;
  receiptLastError: string | null;
  deliveryReceiptAttachmentId: string | null;
  extractionRetryCount: number;
  field3858WrittenAt: string | null;
  field3858RetryCount: number | null;
  field3858GaveUpAt: string | null;
  exceptionNotifiedAt: string | null;
}

export interface AppraisalDeliveryLoanContext {
  id: string;
  loanNumber: string;
  borrowerName: string;
  loanFolder: string | null;
  milestone: string | null;
}

export interface AppraisalDeliveryAttachmentContext {
  attachmentId: string;
  title: string | null;
  uploadedDate: string | null;
  isGeneratedCopy: boolean;
  generatedCopyType: string | null;
  fileSize: number | null;
}

export interface AppraisalDeliveryLinkContext {
  id: string;
  expiresAt: string;
  downloadCount: number;
  firstDownloadedAt: string | null;
  lastDownloadedAt: string | null;
  revokedAt: string | null;
}

export type AppraisalDeliveryMode = 'attachment' | 'link' | 'reminder' | 'not_sent';

export type BorrowerDeliveryResult = 'delivered' | 'skipped' | 'needs_review' | 'processing';

export type BorrowerDeliverySkipReason =
  | 'Not the document we need'
  | 'File was removed from the loan'
  | 'Loan is closed or no longer active'
  | 'Already delivered';

export interface BorrowerDeliveryView {
  result: BorrowerDeliveryResult;
  label: 'Delivered' | 'Skipped' | 'Needs review' | 'Processing';
  why: string;
  followUp: string | null;
  skipReason: BorrowerDeliverySkipReason | null;
}

export interface CollapsedRotationEvidence {
  id: string;
  attachmentId: string;
  documentTitle: string;
  queuedAt: string;
}

export interface AppraisalDeliveryReportRow {
  id: string;
  pipeline: AppraisalPipeline;
  encompassLoanId: string;
  loanId: string | null;
  loanNumber: string;
  borrowerName: string;
  loanFolder: string | null;
  milestone: string | null;
  attachmentId: string;
  documentTitle: string;
  uploadedAt: string | null;
  isRotatedCopy: boolean;
  fileSize: number | null;
  queuedAt: string;
  scheduledFor: string | null;
  /** Raw DB status. Kept for the drawer and support, never a primary label. */
  status: string;
  /** Raw DB reason. Kept for guarded technical detail and history collapse. */
  skipReason: string | null;
  state: AppraisalLifecycleState;
  stage: AppraisalStage;
  stoppedAt: AppraisalStage | null;
  reason: string;
  headline: string;
  guidance: string | null;
  alertable: boolean;
  sentAt: string | null;
  toEmails: string[];
  ccEmails: string[];
  deliveryMode: AppraisalDeliveryMode;
  deliveryLink: AppraisalDeliveryLinkContext | null;
  resendId: string | null;
  receiptAttachmentId: string | null;
  receiptLastError: string | null;
  /** Counted separately. Summing unrelated retry counters means nothing. */
  ocrRetries: number;
  receiptRetries: number;
  field3858Retries: number;
  field3858WrittenAt: string | null;
  lastUpdatedAt: string | null;
  completedAt: string;
  exceptionNotifiedAt: string | null;
  collapsedRotationEvidence: CollapsedRotationEvidence[];
}

const STATE_ORDER: Record<AppraisalLifecycleState, number> = {
  needs_action: 0,
  in_flight: 1,
  delivered: 2,
  excluded: 3
};

function toSnapshot(queue: AppraisalDeliveryQueueRecord) {
  return {
    pipeline: queue.pipeline,
    status: queue.status,
    skipReason: queue.skipReason,
    emailSentAt: queue.emailSentAt,
    receiptNextRetryAt: queue.receiptNextRetryAt,
    receiptLastError: queue.receiptLastError,
    deliveryReceiptAttachmentId: queue.deliveryReceiptAttachmentId,
    field3858WrittenAt: queue.field3858WrittenAt,
    field3858GaveUpAt: queue.field3858GaveUpAt,
    queuedAt: queue.createdAt
  };
}

function deliveryModeFor(
  queue: AppraisalDeliveryQueueRecord,
  link: AppraisalDeliveryLinkContext | null
): AppraisalDeliveryMode {
  if (!queue.emailSentAt) {
    return 'not_sent';
  }
  // CDA ordering emails a processor a reminder. It carries no document, so
  // labelling it "Attachment" (as the first build did) was simply wrong.
  if (queue.pipeline === 'cda_order') {
    return 'reminder';
  }
  return link ? 'link' : 'attachment';
}

export function resolveAppraisalDeliveryRowState(
  queue: AppraisalDeliveryQueueRecord,
  now: Date = new Date()
): AppraisalDeliveryState {
  return resolveAppraisalDeliveryState(toSnapshot(queue), now);
}

export function normalizeAppraisalDeliveryRow(
  input: {
    queue: AppraisalDeliveryQueueRecord;
    loan: AppraisalDeliveryLoanContext | null;
    attachment: AppraisalDeliveryAttachmentContext | null;
    link: AppraisalDeliveryLinkContext | null;
  },
  now: Date = new Date()
): AppraisalDeliveryReportRow {
  const { queue } = input;
  const resolved = resolveAppraisalDeliveryRowState(queue, now);

  return {
    id: queue.id,
    pipeline: queue.pipeline,
    encompassLoanId: queue.encompassLoanId,
    loanId: input.loan?.id || null,
    loanNumber: input.loan?.loanNumber || 'Restricted',
    borrowerName: input.loan?.borrowerName || 'Restricted',
    loanFolder: input.loan?.loanFolder || null,
    milestone: input.loan?.milestone || null,
    attachmentId: queue.attachmentId,
    documentTitle: input.attachment?.title || 'Document unavailable',
    uploadedAt: input.attachment?.uploadedDate || null,
    isRotatedCopy: input.attachment?.isGeneratedCopy === true
      && input.attachment.generatedCopyType === 'rotation',
    fileSize: input.attachment?.fileSize || null,
    queuedAt: queue.createdAt,
    scheduledFor: queue.scheduledFor,
    status: queue.status,
    skipReason: queue.skipReason,
    state: resolved.state,
    stage: resolved.stage,
    stoppedAt: resolved.stoppedAt,
    reason: resolved.reason,
    headline: resolved.headline,
    guidance: resolved.guidance,
    alertable: resolved.alertable,
    sentAt: queue.emailSentAt,
    toEmails: queue.borrowerEmails,
    ccEmails: queue.ccEmails,
    deliveryMode: deliveryModeFor(queue, input.link),
    deliveryLink: input.link,
    resendId: queue.resendId,
    receiptAttachmentId: queue.deliveryReceiptAttachmentId,
    receiptLastError: queue.receiptLastError,
    ocrRetries: queue.extractionRetryCount || 0,
    receiptRetries: queue.receiptRetryCount || 0,
    field3858Retries: queue.field3858RetryCount || 0,
    field3858WrittenAt: queue.field3858WrittenAt,
    lastUpdatedAt: queue.updatedAt,
    completedAt: queue.emailSentAt || queue.decidedAt || queue.updatedAt || queue.createdAt,
    exceptionNotifiedAt: queue.exceptionNotifiedAt,
    collapsedRotationEvidence: []
  };
}

const SKIP_REASON_LABELS: Record<string, BorrowerDeliverySkipReason> = {
  not_a_deliverable_document: 'Not the document we need',
  attachment_not_found: 'File was removed from the loan',
  ineligible_loan_state: 'Loan is closed or no longer active',
  historical_loan_suppressed: 'Loan is closed or no longer active',
  already_handled: 'Already delivered'
};

/**
 * Thin employee-facing projection. Result identity comes only from the shared
 * contract state plus whether the email was sent; raw queue statuses never
 * become a second classification system.
 */
export function borrowerDeliveryViewFor(
  row: Pick<
    AppraisalDeliveryReportRow,
    'state' | 'sentAt' | 'reason' | 'headline'
  >
): BorrowerDeliveryView {
  if (row.sentAt) {
    return {
      result: 'delivered',
      label: 'Delivered',
      why: 'Sent to the borrower',
      followUp: row.state === 'delivered'
        ? null
        : `Follow-up needed: ${row.headline}`,
      skipReason: null
    };
  }

  if (row.state === 'excluded') {
    const skipReason = SKIP_REASON_LABELS[row.reason] || 'Not the document we need';
    return {
      result: 'skipped',
      label: 'Skipped',
      why: skipReason,
      followUp: null,
      skipReason
    };
  }

  if (row.state === 'needs_action') {
    return {
      result: 'needs_review',
      label: 'Needs review',
      why: row.headline,
      followUp: null,
      skipReason: null
    };
  }

  return {
    result: 'processing',
    label: 'Processing',
    why: row.headline,
    followUp: null,
    skipReason: null
  };
}

const CANONICAL_FAMILY_ALREADY_SENT_PREFIX = 'canonical_family_already_sent:';

/**
 * Historical generated-copy rows are evidence for the original delivery, not
 * a second document result. Collapse only when that delivered root is in the
 * loaded range; otherwise retain the skipped row so the visible counts remain
 * honest.
 */
export function collapseBorrowerDeliveryRotations(
  rows: AppraisalDeliveryReportRow[]
): AppraisalDeliveryReportRow[] {
  const collapsedIds = new Set<string>();
  const evidenceByRootId = new Map<string, CollapsedRotationEvidence[]>();

  for (const row of rows) {
    if (!row.skipReason?.startsWith(CANONICAL_FAMILY_ALREADY_SENT_PREFIX)) {
      continue;
    }
    const familyRootAttachmentId = row.skipReason
      .slice(CANONICAL_FAMILY_ALREADY_SENT_PREFIX.length)
      .trim();
    if (!familyRootAttachmentId) {
      continue;
    }
    const root = rows.find(candidate => (
      candidate.id !== row.id
      && candidate.pipeline === row.pipeline
      && candidate.encompassLoanId === row.encompassLoanId
      && candidate.attachmentId === familyRootAttachmentId
      && candidate.sentAt !== null
    ));
    if (!root) {
      continue;
    }
    collapsedIds.add(row.id);
    const evidence = evidenceByRootId.get(root.id) || [];
    evidence.push({
      id: row.id,
      attachmentId: row.attachmentId,
      documentTitle: row.documentTitle,
      queuedAt: row.queuedAt
    });
    evidenceByRootId.set(root.id, evidence);
  }

  return rows
    .filter(row => !collapsedIds.has(row.id))
    .map(row => {
      const evidence = evidenceByRootId.get(row.id);
      return evidence
        ? { ...row, collapsedRotationEvidence: evidence }
        : row;
    });
}

/** Needs-you first, then in flight, then newest. */
export function sortAppraisalDeliveryRows(
  left: AppraisalDeliveryReportRow,
  right: AppraisalDeliveryReportRow
): number {
  const stateDifference = STATE_ORDER[left.state] - STATE_ORDER[right.state];
  if (stateDifference !== 0) {
    return stateDifference;
  }
  return Date.parse(right.queuedAt) - Date.parse(left.queuedAt);
}

export interface AppraisalLoanGroup {
  encompassLoanId: string;
  loanId: string | null;
  loanNumber: string;
  borrowerName: string;
  documents: AppraisalDeliveryReportRow[];
  counts: Record<AppraisalLifecycleState, number>;
  /** The most urgent state present on the loan. */
  state: AppraisalLifecycleState;
  latestQueuedAt: string;
}

/**
 * CDA ordering asks a loan-level question — "does this loan still need a CDA
 * ordered?" — but the queue stores one row per file dropped in the Appraisal
 * folder. Showing 815 document rows to answer a question about ~80 loans is the
 * wrong unit, so this view rolls documents up to the loan they belong to.
 */
export function groupAppraisalRowsByLoan(
  rows: AppraisalDeliveryReportRow[]
): AppraisalLoanGroup[] {
  const groups = new Map<string, AppraisalLoanGroup>();

  for (const row of rows) {
    let group = groups.get(row.encompassLoanId);
    if (!group) {
      group = {
        encompassLoanId: row.encompassLoanId,
        loanId: row.loanId,
        loanNumber: row.loanNumber,
        borrowerName: row.borrowerName,
        documents: [],
        counts: { needs_action: 0, in_flight: 0, delivered: 0, excluded: 0 },
        state: 'excluded',
        latestQueuedAt: row.queuedAt
      };
      groups.set(row.encompassLoanId, group);
    }
    group.documents.push(row);
    group.counts[row.state] += 1;
    if (STATE_ORDER[row.state] < STATE_ORDER[group.state]) {
      group.state = row.state;
    }
    if (Date.parse(row.queuedAt) > Date.parse(group.latestQueuedAt)) {
      group.latestQueuedAt = row.queuedAt;
    }
  }

  for (const group of groups.values()) {
    group.documents.sort(sortAppraisalDeliveryRows);
  }

  return [...groups.values()].sort((left, right) => {
    const stateDifference = STATE_ORDER[left.state] - STATE_ORDER[right.state];
    if (stateDifference !== 0) {
      return stateDifference;
    }
    return Date.parse(right.latestQueuedAt) - Date.parse(left.latestQueuedAt);
  });
}
